import React, { useRef, useState } from 'react';
import ImageUploader from './ImageUploader';
import useFileDrop from '../hooks/useFileDrop';
import './FieldImageGallery.css';

const formatBytes = (bytes, decimals = 1) => {
  if (!bytes || bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(decimals)) + ' ' + sizes[i];
};

// Ordered gallery of a field's images for the editor: a row of thumbnails,
// each with its own delete and reorder controls, plus an upload button that
// appends to the end of the gallery. Thumbnails reorder by dragging too, and
// files dropped anywhere on the gallery upload like picked ones.
const FieldImageGallery = ({
  images, // [{ id, url, size, caption, attribution, blurred }] in gallery order
  locationId,
  fieldType,
  questId,
  maxImages = 10,
  onImageUploaded, // () => void, gallery data changed on the server
  onDeleteImage, // (image) => void
  onEditCaption, // (image) => void
  onToggleBlur, // (image) => void; omit to hide the per-image blur checkbox
  onReorder, // (orderedIds) => void
  onThumbnailClick, // (index) => void
  disabled,
}) => {
  const move = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= images.length) return;
    const ids = images.map((img) => img.id);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    onReorder(ids);
  };

  const uploaderRef = useRef(null);
  const { isDragOver, dropProps } = useFileDrop(
    (files) => uploaderRef.current && uploaderRef.current.uploadFiles(files),
    { disabled: disabled || images.length >= maxImages },
  );

  // Thumbnail drag: the source index lives in a ref of this gallery, so a
  // thumbnail from another field's gallery is never accepted here.
  const dragFromRef = useRef(null);
  const [dragFrom, setDragFrom] = useState(null);
  const [dropBefore, setDropBefore] = useState(null); // insertion slot 0..images.length

  const canDragThumbs = !disabled && images.length > 1;

  const endThumbDrag = () => {
    dragFromRef.current = null;
    setDragFrom(null);
    setDropBefore(null);
  };

  const handleThumbDragStart = (e, index) => {
    // Keep the location card or anything around from treating this as its drag.
    e.stopPropagation();
    dragFromRef.current = index;
    setDragFrom(index);
    e.dataTransfer.effectAllowed = 'move';
    // Firefox starts a drag only when some data is set.
    e.dataTransfer.setData('text/plain', String(images[index].id));
  };

  const handleThumbDragOver = (e, index) => {
    if (dragFromRef.current === null) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    // Left half of a thumbnail inserts before it, right half after it.
    const rect = e.currentTarget.getBoundingClientRect();
    const slot = e.clientX < rect.left + rect.width / 2 ? index : index + 1;
    if (slot !== dropBefore) setDropBefore(slot);
  };

  const handleThumbDrop = (e) => {
    if (dragFromRef.current === null) return;
    e.preventDefault();
    e.stopPropagation();
    const from = dragFromRef.current;
    const slot = dropBefore;
    endThumbDrag();
    if (slot === null) return;
    const to = slot > from ? slot - 1 : slot;
    if (to === from) return;
    const ids = images.map((img) => img.id);
    const [moved] = ids.splice(from, 1);
    ids.splice(to, 0, moved);
    onReorder(ids);
  };

  const itemClass = (i) => {
    const cls = ['gallery-item'];
    if (canDragThumbs) cls.push('is-draggable');
    if (dragFrom === i) cls.push('is-dragging');
    if (dragFrom !== null && dropBefore === i && i !== dragFrom && i !== dragFrom + 1) cls.push('drop-before');
    if (dragFrom !== null && dropBefore === i + 1 && i === images.length - 1 && i !== dragFrom) cls.push('drop-after');
    return cls.join(' ');
  };

  const totalSize = images.reduce((acc, img) => acc + (Number(img.size) || 0), 0);

  return (
    <div className={isDragOver ? 'field-image-gallery is-drop-target' : 'field-image-gallery'} {...dropProps}>
      {images.map((img, i) => (
        <div
          className={itemClass(i)}
          key={img.id}
          draggable={canDragThumbs}
          onDragStart={canDragThumbs ? (e) => handleThumbDragStart(e, i) : undefined}
          onDragOver={(e) => handleThumbDragOver(e, i)}
          onDrop={handleThumbDrop}
          onDragEnd={endThumbDrag}
        >
          <div
            className="field-thumbnail"
            onClick={() => onThumbnailClick(i)}
            title={`Image ${i + 1} of ${images.length}${img.size ? ` (${formatBytes(img.size)})` : ''}${img.caption ? `\n“${img.caption}”` : ''}${img.attribution ? `\n© ${img.attribution}` : ''} — click to view${canDragThumbs ? ', drag to reorder' : ''}`}
          >
            <img
              draggable={false}
              src={img.url}
              alt={img.caption || `Thumbnail ${i + 1}`}
              className={img.blurred ? 'is-blurred' : undefined}
            />
          </div>
          <div className="gallery-item-controls">
            <button
              className="gallery-ctrl"
              onClick={() => move(i, -1)}
              disabled={disabled || i === 0}
              title="Move left"
            >
              ‹
            </button>
            <button
              className={img.caption || img.attribution ? 'gallery-ctrl gallery-ctrl-caption has-caption' : 'gallery-ctrl gallery-ctrl-caption'}
              onClick={() => onEditCaption(img)}
              disabled={disabled}
              title={`${img.caption ? `Edit caption: “${img.caption}”` : 'Add caption'}${img.attribution ? `\nAttribution: ${img.attribution}` : ' and attribution'}`}
            >
              ✎
            </button>
            <button
              className="gallery-ctrl gallery-ctrl-delete"
              onClick={() => onDeleteImage(img)}
              disabled={disabled}
              title="Delete this image"
            >
              ×
            </button>
            <button
              className="gallery-ctrl"
              onClick={() => move(i, 1)}
              disabled={disabled || i === images.length - 1}
              title="Move right"
            >
              ›
            </button>
          </div>
          {onToggleBlur && (
            <label
              className="gallery-blur-toggle"
              title="The app shows this photo blurred (thumbnail and fullscreen) until the player taps it"
            >
              <input
                type="checkbox"
                checked={!!img.blurred}
                onChange={() => onToggleBlur(img)}
                disabled={disabled}
              />
              blur
            </label>
          )}
        </div>
      ))}
      <ImageUploader
        ref={uploaderRef}
        locationId={locationId}
        fieldType={fieldType}
        questId={questId}
        onImageUploaded={onImageUploaded}
        imageCount={images.length}
        maxImages={maxImages}
        disabled={disabled}
      />
      {images.length > 0 && (
        <span className="file-size">
          ({images.length > 1 ? `${images.length} images, ` : ''}{formatBytes(totalSize)})
        </span>
      )}
    </div>
  );
};

export default FieldImageGallery;
